import type { ReactElement } from 'react';
import type {
  MCPServerUsersResponse,
  MCPServerUser,
} from '@kirin/api-client/contracts/mcp';
import { TableCell, TableRow } from '@/components/ui/table';
import { useMCPServerUsers } from '@/hooks/api/useMCPServers';
import { MCPSourceBadges } from '@/components/shared/mcp';
import { StatusDot } from '@/components/shared/StatusDot';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { InventoryUserListDialog } from './InventoryUserListDialog';

interface MCPUserListModalProps {
  organizationId: string;
  serverName: string | null;
  onClose: () => void;
}

/**
 * Users who have a specific MCP row configured. Columns: user, sources,
 * handshake name(s) (KIR-2383), status, last seen.
 */
export function MCPUserListModal({
  organizationId,
  serverName,
  onClose,
}: MCPUserListModalProps): ReactElement {
  const { data, isLoading, error } = useMCPServerUsers(
    organizationId,
    serverName || ''
  ) as {
    data: MCPServerUsersResponse | undefined;
    isLoading: boolean;
    error: unknown;
  };
  const users = data?.users ?? [];

  return (
    <InventoryUserListDialog
      open={serverName !== null}
      onClose={onClose}
      title={
        <>
          Users with <span className="font-mono">{serverName}</span>
        </>
      }
      columns={[
        { label: 'User' },
        { label: 'Sources' },
        { label: 'Handshake Name' },
        { label: 'Status', className: 'text-center' },
        { label: 'Last Seen' },
      ]}
      isLoading={isLoading}
      error={error}
      isEmpty={users.length === 0}
      emptyMessage="No users found with this MCP server"
    >
      {users.map((user: MCPServerUser) => {
        const handshakeNames = user.serverNames.join(', ');
        return (
          <TableRow key={user.userId}>
            <TableCell>
              <div>
                <div className="font-medium">{user.name || 'Unknown User'}</div>
                {user.email && (
                  <div className="text-sm text-muted-foreground">
                    {user.email}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell>
              <MCPSourceBadges sources={user.sources} />
            </TableCell>
            <TableCell
              className="text-sm font-mono text-muted-foreground max-w-[16rem] truncate"
              title={handshakeNames || undefined}
            >
              {handshakeNames || '—'}
            </TableCell>
            <TableCell className="text-center">
              <div className="flex justify-center">
                <StatusDot
                  isActive={user.isRunning}
                  activeLabel="Running"
                  inactiveLabel="Offline"
                  showLabel={true}
                />
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
              <RelativeTimeWithTooltip
                date={user.lastSeenAt}
                showIcon={false}
              />
            </TableCell>
          </TableRow>
        );
      })}
    </InventoryUserListDialog>
  );
}
